import { Component, inject } from '@angular/core';
import { ClosetDoorsStandardService } from 'src/app/services/configurators-2d/applications/closet-doors-standard.service';
import { CurrentService } from 'src/app/services/current.service';
import { Color } from 'src/app/models/configurators-2d/applications/closet-doors-standard/color';
import { Door } from 'src/app/models/configurators-2d/applications/closet-doors-standard/door';
import { DoorSection } from 'src/app/models/configurators-2d/applications/closet-doors-standard/door-section';
import { FinishesSubStep } from '../../models/finishes-sub-step.enum';
import { FinishesSubStepService } from '../../services/finishes-sub-step.service';

@Component({
  selector: 'ng-casto-closet-doors-standard-finishes-colors',
  templateUrl: './colors.component.html',
  styleUrls: ['./colors.component.scss'],
})
export class ClosetDoorsStandardFinishesColorsComponent {
  readonly SubStep = FinishesSubStep;

  private readonly closetDoorsService = inject(ClosetDoorsStandardService);
  private readonly current = inject(CurrentService);
  private readonly subStepService = inject(FinishesSubStepService);

  readonly baseImgURL = this.closetDoorsService.apiUrl;

  // ---------------------------------------------------------------------------
  // Getters
  // ---------------------------------------------------------------------------

  get subStep(): FinishesSubStep {
    return this.subStepService.subStep();
  }

  get decorations(): Color[] {
    return this.closetDoorsService.decorations;
  }

  get selectedDoorId(): number | undefined {
    return this.current.closetDoors.selectedDoorId;
  }

  /** Index (1-based) de la section courante sélectionnée dans la preview. */
  get selectedSectionIndex(): number | undefined {
    return this.current.closetDoors.selectedSectionIndex;
  }

  get doors(): Door[] {
    return this.current.closetDoors.doors;
  }

  /**
   * Décors disponibles pour la section courante, avec filtrage restreintes.
   */
  get filteredDecorations(): Color[] {
    const section = this.currentSection;
    const sectionIds: number[] = section?.availableColorIds ?? [];
    if (sectionIds.length === 0) return this.decorations ?? [];

    return (this.decorations ?? []).filter((d: Color) => sectionIds.includes(d.id));
  }

  /** Id de la couleur de la section courante — pilote l'affichage du bouton "appliquer à tous". */
  get selectedDecorationId(): number | undefined {
    return this.currentSection?.color?.id;
  }

  get selectedDecoration(): Color | undefined {
    const colorId = this.selectedDecorationId;
    return this.closetDoorsService.decorations?.find((d: Color) => d.id === colorId);
  }

  /** Section courante sélectionnée (porte + section). */
  private get currentSection(): DoorSection | undefined {
    if (!this.selectedDoorId || !this.selectedSectionIndex) return undefined;
    const door = this.doors.find((d: Door) => d.id === this.selectedDoorId);
    return door?.sections?.find((s: DoorSection) => s.index === this.selectedSectionIndex);
  }

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  toggleCollapse(): void {
    this.subStepService.toggle(FinishesSubStep.Colors);
    // Si la carte vient de s'ouvrir, auto-sélectionner le premier vantail et sa première section configurable
    if (this.subStepService.subStep() === FinishesSubStep.Colors) {
      const doors: Door[] = this.current.closetDoors.doors;
      if (doors.length > 0) {
        this.current.closetDoors.selectedDoorId = doors[0].id;
        const firstConfigurable = (doors[0].sections ?? []).find((s: DoorSection) => !s.isFixed);
        this.current.closetDoors.selectedSectionIndex = firstConfigurable?.index;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Sélection
  // ---------------------------------------------------------------------------

  /**
   * Sélectionne un décor pour la section courante, puis désélectionne la section
   * pour rendre le crayon visible à nouveau.
   */
  selectDecoration(decoration: Color): void {
    if (decoration.isDisabled) return;
    if (!this.selectedDoorId || !this.selectedSectionIndex) return;
    const door = this.doors.find((d: Door) => d.id === this.selectedDoorId);
    if (!door) return;
    const section = door.sections?.find((s: DoorSection) => s.index === this.selectedSectionIndex);
    if (!section || section.isFixed) return;

    const ids: number[] = section.availableColorIds ?? [];
    if (ids.length > 0 && !ids.includes(decoration.id)) return;

    section.color = decoration;
    this.applyToLinkedSections(door, section, decoration);
    this.scrollDown();
  }

  /**
   * Propage le décor aux sections du même vantail liées à `section` (remplissage à l'identique),
   * en respectant les sections fixes et les restrictions de couleur par section.
   */
  private applyToLinkedSections(door: Door, section: DoorSection, decoration: Color): void {
    const linked: number[] = section.linkedSectionIndexes ?? [];
    if (linked.length === 0) return;

    (door.sections ?? []).forEach((s: DoorSection) => {
      if (!linked.includes(s.index) || s.isFixed) return;

      const ids: number[] = s.availableColorIds ?? [];
      if (ids.length === 0 || ids.includes(decoration.id)) {
        s.color = decoration;
      }
    });
  }

  /**
   * Applique le décor de la section courante à toutes les sections configurables
   * compatibles sur tous les vantaux.
   */
  applyAll(): void {
    if (this.selectedDecorationId === undefined) return;
    const colorId = this.selectedDecorationId;
    const decoration = this.closetDoorsService.decorations?.find((d: Color) => d.id === colorId);

    // On n'applique pas la couleur partout si elle est introuvable ou désactivée
    if (!decoration || decoration.isDisabled) return;

    this.current.closetDoors.doors.forEach((door: Door) => {
      (door.sections ?? []).forEach((section: DoorSection) => {
        if (section.isFixed) return;
        const ids: number[] = section.availableColorIds ?? [];
        if (ids.length === 0 || ids.includes(colorId)) {
          section.color = decoration;
        }
      });
    });
  }

  /**
   * Indique si le décor est sélectionné pour la section courante.
   */
  isDecorationSelected(id: number): boolean {
    return this.currentSection?.color?.id === id;
  }

  /**
   * Indique si le décor est désactivé pour la section courante.
   * Un décor est désactivé si :
   * - il n'est pas dans la liste des couleurs disponibles pour la section (restriction par section), OU
   * - le coloris a été retiré du catalogue fournisseur (`isDisabled`).
   */
  isDecorationDisabled(decoration: Color): boolean {
    if (decoration.isDisabled) return true;
    const section = this.currentSection;
    if (!section) return true;
    const ids: number[] = section.availableColorIds ?? [];
    return ids.length > 0 && !ids.includes(decoration.id);
  }

  // ---------------------------------------------------------------------------
  // Utilitaires privées
  // ---------------------------------------------------------------------------

  private scrollDown(): void {
    if (document.documentElement.clientWidth < 767) {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  }
}
